import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock, Loader2, Mail } from "lucide-react";
import { Alert } from "../components/ui/Feedback";
import { useAuth } from "../app/AuthProvider";

/**
 * Standalone screen — no sidebar, no header.
 *
 * Split layout: a near-black brand panel with a soft violet glow on the
 * left, and a single white credentials card on a pale page on the right.
 * The brand panel drops away below `lg` so the card is the whole screen on
 * a phone.
 */
export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
      const redirectTo = location.state?.from || "/";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleForgotPassword(event) {
    event.preventDefault();
    setError(null);
    setNotice(
      "Accounts are managed by an administrator. Contact them to reset your password.",
    );
  }

  return (
    <div className="flex min-h-screen bg-paper">
      {/* Left — brand panel. Decorative only, hidden below lg. */}
      <div className="relative hidden w-1/2 overflow-hidden bg-ink-950 lg:block">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(45% 40% at 52% 56%, rgba(124,58,237,0.20), rgba(124,58,237,0.07) 55%, transparent 80%)",
          }}
          aria-hidden="true"
        />
        <div className="relative px-[62px] pt-[72px]">
          <h1 className="max-w-[10ch] text-[3.75rem] font-bold leading-[1.1] tracking-[-0.03em] text-white xl:max-w-none">
            Elevate your
            <br />
            trademark strategy.
          </h1>
          <p className="mt-9 max-w-[30rem] text-[1.1875rem] leading-relaxed text-ink-400">
            Join thousands of founders and brands securing their names in
            minutes, not weeks. Bank-level security meets cutting-edge AI.
          </p>
        </div>
      </div>

      {/* Right — credentials card. */}
      <div className="flex flex-1 items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-[440px] rounded-soft border border-ink-100 bg-white px-6 py-12 shadow-float sm:px-10">
          <h2 className="text-center text-[1.875rem] font-bold leading-tight tracking-tight text-ink-900">
            Welcome back
          </h2>
          <p className="mt-3 text-center text-[0.9375rem] text-ink-500">
            Please enter your details to sign in.
          </p>

          <form onSubmit={handleSubmit} className="mt-10" noValidate>
            {error && (
              <div className="mb-5">
                <Alert tone="error">{error}</Alert>
              </div>
            )}
            {notice && (
              <div className="mb-5">
                <Alert tone="info">{notice}</Alert>
              </div>
            )}

            <div className="mb-6">
              <label
                htmlFor="login-email"
                className="mb-2 block text-sm font-medium text-ink-800"
              >
                Email Address
              </label>
              <div className="relative">
                <Mail
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  id="login-email"
                  type="text"
                  inputMode="email"
                  autoComplete="username"
                  autoFocus
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="name@company.com"
                  className="control h-[47px] pl-10 text-[0.9375rem]"
                />
              </div>
            </div>

            <div className="mb-8">
              <div className="mb-2 flex items-center justify-between">
                <label
                  htmlFor="login-password"
                  className="block text-sm font-medium text-ink-800"
                >
                  Password
                </label>
                {/* <a
                  href="/forgot-password"
                  onClick={handleForgotPassword}
                  className="text-[0.8125rem] font-medium text-[#8f5eeb]-500 hover:text-[#8f5eeb]-600"
                >
                  Forgot password?
                </a> */}
              </div>
              <div className="relative">
                <Lock
                  className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                  aria-hidden="true"
                />
                <input
                  id="login-password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="control h-[47px] pl-10 text-[0.9375rem]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={!email.trim() || !password || loading}
              className="flex h-[50px] w-full items-center justify-center gap-2 rounded-lg bg-[#8f5eeb] text-base font-bold text-white
                transition-colors hover:bg-[#8f5eeb]/80 active:bg-[#8f5eeb]
                disabled:cursor-not-allowed disabled:bg-[#8f5eeb]/40"
            >
              {loading && (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="mt-9 text-center">
            <Link
              to="https://getmyfirsttrademark.com/"
              className="inline-flex items-center gap-2 text-[0.9375rem] font-medium text-ink-500 hover:text-ink-800"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
